import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Users as UsersIcon, Plus, Building2, Pencil, Trash2, X, Save, Calendar, ShieldAlert, AlertTriangle, Loader2, FileText, Bell, ExternalLink, MapPin, Gauge, QrCode } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { CATEGORY_LABEL, ExpiryBadge } from '@/components/DocumentenSection';
import InstructiebladenSection from '@/components/InstructiebladenSection';

const ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
  multi_location: 'Multi-location',
  single_location: 'Bedrijfsleider',
  medewerker: 'Medewerker (portal)',
};

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-slate-100" data-testid="modal-close">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function LandingScopeTab({ onFeedback }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/system-config/default_landing_scope')
      .then((r) => { if (!cancelled) setConfig(r.data.value || {}); })
      .catch((e) => {
        if (e.response?.status === 404) {
          if (!cancelled) setConfig({ admin: 'all', multi_location: 'all', single_location: 'own' });
        } else {
          console.error(e);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const update = (role, val) => {
    setConfig((prev) => ({ ...prev, [role]: val }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/system-config/default_landing_scope', { value: config });
      onFeedback?.({ type: 'success', msg: 'Landingslocatie opgeslagen' });
    } catch (e) {
      onFeedback?.({ type: 'error', msg: 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Laden…
      </div>
    );
  }

  const Option = ({ role, label, fixed }) => (
    <div className="flex items-center justify-between border-b border-slate-100 py-3 last:border-0">
      <div>
        <div className="font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">Rol: {role}</div>
      </div>
      <div>
        {fixed ? (
          <span className="text-sm font-medium text-slate-500 px-3 py-1.5 bg-slate-50 rounded-md border border-slate-200">Alle locaties</span>
        ) : (
          <select 
            value={config[role] || 'all'} 
            onChange={(e) => update(role, e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-tw1-accent-gold/40"
          >
            <option value="all">Alle locaties</option>
            <option value="own">Eigen toegewezen locatie</option>
          </select>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-landingscope-content">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <MapPin size={18} /> Standaard landingslocatie per rol
          </h3>
          <button
            onClick={save}
            disabled={saving}
            data-testid="btn-save-landingscope"
            className="flex items-center gap-1.5 rounded-md bg-tw1-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-tw1-brand-navy-hover disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-6">
          Kies welke locatie geselecteerd is wanneer een gebruiker voor het eerst inlogt. (Indien 'Eigen toegewezen locatie' geselecteerd is maar niet toegewezen, vallen we terug op alle locaties).
        </p>
        
        <div className="rounded-lg border border-slate-200 px-4">
          <Option role="owner" label="Eigenaar" fixed={true} />
          <Option role="admin" label="Beheerder" fixed={false} />
          <Option role="multi_location" label="Bedrijfsleider (Multi-locatie)" fixed={false} />
          <Option role="single_location" label="Bedrijfsleider (Enkele locatie)" fixed={false} />
        </div>
      </div>
    </div>
  );
}

function UrenGuardTab({ onFeedback }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [budgets, setBudgets] = useState({}); // slug -> {min, max}
  const [savingBudget, setSavingBudget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get('/system-config/roster_hours_guard'),
      api.get('/locations').catch(() => ({ data: [] })),
    ])
      .then(([r, locs]) => {
        if (cancelled) return;
        const cfg = r.data.value || {};
        setConfig(cfg);
        setLocations(locs.data || []);
        const perLoc = cfg.per_location || {};
        const b = {};
        (locs.data || []).forEach((l) => {
          b[l.slug] = {
            min: perLoc[l.slug]?.budget_min ?? '',
            max: perLoc[l.slug]?.budget_max ?? '',
          };
        });
        setBudgets(b);
      })
      .catch(() => { if (!cancelled) setConfig({ enabled: true, tolerance_hours: 4, tolerance_pct: null }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const saveBudget = async (slug) => {
    setSavingBudget(slug);
    try {
      const b = budgets[slug] || {};
      await api.put('/roster-guard/budget', {
        location_slug: slug,
        budget_min: b.min === '' || b.min == null ? null : Number(b.min),
        budget_max: b.max === '' || b.max == null ? null : Number(b.max),
      });
      onFeedback?.({ type: 'success', msg: `Uren-budget opgeslagen voor ${slug}` });
    } catch (e) {
      const s = e.response?.status;
      onFeedback?.({ type: 'error', msg: s === 403 ? 'Geen rechten om budget in te stellen' : (e.response?.data?.detail || 'Budget opslaan mislukt') });
    } finally {
      setSavingBudget(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!config.enabled,
        tolerance_hours: config.tolerance_hours === '' || config.tolerance_hours == null ? null : Number(config.tolerance_hours),
        tolerance_pct: config.tolerance_pct === '' || config.tolerance_pct == null ? null : Number(config.tolerance_pct),
        per_location: config.per_location || {},
      };
      await api.put('/system-config/roster_hours_guard', { value: payload });
      onFeedback?.({ type: 'success', msg: 'Uren-guard opgeslagen' });
    } catch (e) {
      onFeedback?.({ type: 'error', msg: 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Laden…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tab-urenguard-content">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle size={18} /> Uren-guard (min/max gepland vs contract)
          </h3>
          <button
            onClick={save} disabled={saving}
            data-testid="btn-save-urenguard"
            className="flex items-center gap-1.5 rounded-md bg-tw1-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-tw1-brand-navy-hover disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Signaleert wanneer een vaste medewerker te veel afwijkt van zijn contracturen in een week (onder-/overbezet).
          Oproepkrachten worden nooit gemarkeerd. Let op: de exacte norm wordt nog door Johannes vastgesteld — pas de bandbreedte hier aan.
        </p>

        <label className="flex items-center gap-2 mb-4 text-sm text-slate-800">
          <input
            type="checkbox" checked={!!config.enabled} data-testid="urenguard-enabled"
            onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))}
          />
          Guard ingeschakeld
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Bandbreedte (± uren)</label>
            <input
              type="number" min="0" step="0.5" data-testid="urenguard-tolerance-hours"
              value={config.tolerance_hours ?? ''}
              onChange={(e) => setConfig((c) => ({ ...c, tolerance_hours: e.target.value }))}
              className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Bandbreedte (± %, optioneel)</label>
            <input
              type="number" min="0" step="1" data-testid="urenguard-tolerance-pct"
              value={config.tolerance_pct ?? ''}
              placeholder="—"
              onChange={(e) => setConfig((c) => ({ ...c, tolerance_pct: e.target.value }))}
              className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            />
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">
          Bij invullen van beide wordt de ruimste (meest tolerante) bandbreedte gebruikt. Per-locatie afwijkingen kunnen later worden toegevoegd.
        </p>
      </div>

      {/* Per-location weekly TOTAL-hours budget (OM+ control, on top of the per-employee band) */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="urenbudget-section">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-1">
          <Gauge size={18} /> Uren-budget per locatie (min/max totaal gepland)
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Stel een minimum en maximum aantal totaal geplande uren in voor de weekplanning van een locatie. De roostermaker ziet
          dit terug en de OM krijgt een melding bij over-/onderschrijding. Alleen instelbaar door OM en hoger. Laat leeg om uit te schakelen.
        </p>
        <div className="space-y-2">
          {locations.length === 0 && <p className="text-sm text-slate-400">Geen locaties beschikbaar.</p>}
          {locations.map((l) => (
            <div key={l.slug} data-testid={`urenbudget-row-${l.slug}`} className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-100 bg-slate-50/60 p-3">
              <div className="min-w-[140px] flex-1">
                <span className="text-sm font-medium text-slate-800">{l.name}</span>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-0.5">Min (u)</label>
                <input
                  type="number" min="0" step="1" data-testid={`urenbudget-min-${l.slug}`}
                  value={budgets[l.slug]?.min ?? ''} placeholder="—"
                  onChange={(e) => setBudgets((b) => ({ ...b, [l.slug]: { ...b[l.slug], min: e.target.value } }))}
                  className="w-24 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-0.5">Max (u)</label>
                <input
                  type="number" min="0" step="1" data-testid={`urenbudget-max-${l.slug}`}
                  value={budgets[l.slug]?.max ?? ''} placeholder="—"
                  onChange={(e) => setBudgets((b) => ({ ...b, [l.slug]: { ...b[l.slug], max: e.target.value } }))}
                  className="w-24 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
                />
              </div>
              <button
                onClick={() => saveBudget(l.slug)} disabled={savingBudget === l.slug}
                data-testid={`btn-save-budget-${l.slug}`}
                className="flex items-center gap-1.5 rounded-md border border-tw1-brand-navy px-3 py-1.5 text-sm font-medium text-tw1-brand-navy hover:bg-tw1-brand-navy/5 disabled:opacity-60"
              >
                {savingBudget === l.slug ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Opslaan
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Loonperiode-kalender (self-configurable generator + editable proposal) ──
function isoWeekMonday(year, week) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dow = jan4.getUTCDay() || 7; // Mon=1 .. Sun=7
  const mondayW1 = new Date(jan4);
  mondayW1.setUTCDate(jan4.getUTCDate() - (dow - 1));
  const monday = new Date(mondayW1);
  monday.setUTCDate(mondayW1.getUTCDate() + (week - 1) * 7);
  return monday;
}
function lastThursday(year, monthIdx0) {
  // monthIdx0: 0-11. Last Thursday of that month.
  const d = new Date(Date.UTC(year, monthIdx0 + 1, 0)); // last day of month
  while (d.getUTCDay() !== 4) d.setUTCDate(d.getUTCDate() - 1);
  return d;
}
function fmtDate(d) {
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getUTCFullYear()}`;
}
function deriveDates(weeks) {
  const first = weeks[0];
  const last = weeks[weeks.length - 1];
  const monday = isoWeekMonday(first[0], first[1]);
  const sunday = new Date(isoWeekMonday(last[0], last[1]));
  sunday.setUTCDate(sunday.getUTCDate() + 6);
  const pay = lastThursday(sunday.getUTCFullYear(), sunday.getUTCMonth());
  return { start_date: fmtDate(monday), end_date: fmtDate(sunday), payment_date: fmtDate(pay) };
}

function LoonperiodeKalenderTab({ onFeedback }) {
  const nextYear = new Date().getFullYear() + 1;
  const yearOptions = [];
  for (let y = 2026; y <= nextYear + 4; y++) yearOptions.push(y);
  const [year, setYear] = useState(nextYear);
  const [data, setData] = useState(null); // {status, locked, editable, periods, version}
  const [periods, setPeriods] = useState([]); // editable weeks model
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [years, setYears] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: list }] = await Promise.all([
        api.get(`/loonperiode-kalender/${year}`),
        api.get('/loonperiode-kalender'),
      ]);
      setData(d);
      setPeriods(d.periods.map((p) => ({ period: p.period, weeks: p.weeks.map((w) => [...w]) })));
      setYears(list.years || []);
    } catch (e) {
      onFeedback({ type: 'error', msg: 'Kon loonperiode-kalender niet laden' });
    } finally {
      setLoading(false);
    }
  }, [year, onFeedback]);

  useEffect(() => { load(); }, [load]);

  function shiftBoundary(idx, dir) {
    // idx = boundary between period idx and idx+1 (0-based). dir +1 = period idx+1 grows.
    setPeriods((prev) => {
      const next = prev.map((p) => ({ ...p, weeks: p.weeks.map((w) => [...w]) }));
      if (dir > 0) {
        // move the FIRST week of period idx+1 to the END of period idx
        if (next[idx + 1].weeks.length <= 1) return prev;
        const moved = next[idx + 1].weeks.shift();
        next[idx].weeks.push(moved);
      } else {
        // move the LAST week of period idx to the FRONT of period idx+1
        if (next[idx].weeks.length <= 1) return prev;
        const moved = next[idx].weeks.pop();
        next[idx + 1].weeks.unshift(moved);
      }
      return next;
    });
  }

  const validation = (() => {
    const errors = [];
    periods.forEach((p) => {
      if (p.weeks.length < 4 || p.weeks.length > 5) {
        errors.push(`Periode ${p.period}: ${p.weeks.length} weken (alleen 4 of 5 toegestaan)`);
      }
    });
    return errors;
  })();
  const canSave = data?.editable && validation.length === 0 && !saving;

  async function regenerate() {
    try {
      const { data: d } = await api.post(`/loonperiode-kalender/${year}/generate`);
      setData((cur) => ({ ...cur, ...d }));
      setPeriods(d.periods.map((p) => ({ period: p.period, weeks: p.weeks.map((w) => [...w]) })));
      onFeedback({ type: 'success', msg: `Nieuw voorstel gegenereerd voor ${year}` });
    } catch (e) {
      onFeedback({ type: 'error', msg: e.response?.data?.detail || 'Genereren mislukt' });
    }
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { periods: periods.map((p) => ({ period: p.period, weeks: p.weeks })) };
      await api.put(`/loonperiode-kalender/${year}`, payload);
      onFeedback({ type: 'success', msg: `Loonperiode-kalender ${year} vastgesteld` });
      await load();
    } catch (e) {
      onFeedback({ type: 'error', msg: e.response?.data?.detail || 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tab-content-loonperiode-kalender">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Calendar size={18} /> Loonperiode-kalender
        </h3>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500">Loonjaar</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="kalender-year-select"
            className="rounded-md border border-slate-200 px-3 py-1.5 text-sm"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-sm text-slate-500 mb-4">
        Betaling = laatste donderdag van de maand. De week met de betaaldonderdag valt in een latere periode.
        Periodes zijn aaneengesloten hele ISO-weken (4 of 5). 2026 is vastgesteld en vergrendeld.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="animate-spin" size={16} /> Laden…</div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            {data?.locked ? (
              <span data-testid="kalender-status" className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                <ShieldAlert size={12} /> Vastgesteld · vergrendeld
              </span>
            ) : data?.status === 'vastgesteld' ? (
              <span data-testid="kalender-status" className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                Vastgesteld
              </span>
            ) : (
              <span data-testid="kalender-status" className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                Concept (voorstel)
              </span>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kalender-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Periode</th>
                  <th className="py-2 pr-3">ISO-weken</th>
                  <th className="py-2 pr-3">Start</th>
                  <th className="py-2 pr-3">Einde</th>
                  <th className="py-2 pr-3">Betaaldatum</th>
                  <th className="py-2 pr-3">Weken</th>
                  {data?.editable && <th className="py-2 pr-3">Grens</th>}
                </tr>
              </thead>
              <tbody>
                {periods.map((p, idx) => {
                  const d = deriveDates(p.weeks);
                  const wk = p.weeks;
                  const label = `wk ${wk[0][1]}–${wk[wk.length - 1][1]}`;
                  const bad = wk.length < 4 || wk.length > 5;
                  return (
                    <tr key={p.period} className="border-b border-slate-100" data-testid={`kalender-row-${p.period}`}>
                      <td className="py-2 pr-3 font-medium text-brand-navy">P{p.period}</td>
                      <td className="py-2 pr-3 text-slate-600">{label}</td>
                      <td className="py-2 pr-3 text-slate-600">{d.start_date}</td>
                      <td className="py-2 pr-3 text-slate-600">{d.end_date}</td>
                      <td className="py-2 pr-3 text-slate-600">{d.payment_date}</td>
                      <td className={`py-2 pr-3 font-medium ${bad ? 'text-red-600' : 'text-slate-700'}`}>{wk.length}</td>
                      {data?.editable && (
                        <td className="py-2 pr-3">
                          {p.period < 12 && (
                            <span className="inline-flex items-center gap-1">
                              <button
                                onClick={() => shiftBoundary(idx, -1)}
                                data-testid={`kalender-shift-earlier-${p.period}`}
                                title="Grens een week eerder"
                                className="rounded border border-slate-200 px-1.5 py-0.5 text-xs hover:bg-slate-50"
                              >−</button>
                              <button
                                onClick={() => shiftBoundary(idx, 1)}
                                data-testid={`kalender-shift-later-${p.period}`}
                                title="Grens een week later"
                                className="rounded border border-slate-200 px-1.5 py-0.5 text-xs hover:bg-slate-50"
                              >+</button>
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {validation.length > 0 && (
            <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="kalender-validation">
              <div className="flex items-center gap-1 font-medium"><AlertTriangle size={14} /> Niet geldig:</div>
              <ul className="list-disc pl-5 mt-1">
                {validation.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}

          {data?.editable ? (
            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={regenerate}
                data-testid="kalender-generate-btn"
                className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Voorstel genereren
              </button>
              <button
                onClick={save}
                disabled={!canSave}
                data-testid="kalender-save-btn"
                className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-50"
              >
                {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Vaststellen
              </button>
            </div>
          ) : (
            <div className="mt-4 text-sm text-slate-500">Dit loonjaar is vastgesteld en vergrendeld (alleen-lezen).</div>
          )}

          {years.length > 0 && (
            <div className="mt-6 border-t border-slate-100 pt-3 text-xs text-slate-400">
              Vastgestelde loonjaren: {years.map((y) => `${y.loon_year}${y.locked ? ' (vergrendeld)' : ''}`).join(' · ')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const TARIEF_GROEPEN = [
  {
    titel: 'Vakantie',
    velden: [
      { k: 'vakantiegeld_pct', label: 'Vakantiegeld %' },
      { k: 'vakantiedagen_toeslag_pct', label: 'Vakantiedagen-toeslag % (oproep)' },
    ],
  },
  {
    titel: 'Werkgeverspremies',
    velden: [
      { k: 'awf_laag', label: 'AWf laag % (vast)' },
      { k: 'awf_hoog', label: 'AWf hoog % (oproep)' },
      { k: 'aof_pct', label: 'Aof %' },
      { k: 'zvw_pct', label: 'Zvw %' },
      { k: 'whk_pct', label: 'Whk %' },
      { k: 'kinderopvang_pct', label: 'Kinderopvang %' },
      { k: 'rvu_pct', label: 'RVU %' },
      { k: 'sociaal_fonds_pct', label: 'Sociaal fonds %' },
      { k: 'opl_ontw_pct', label: 'Opleiding & ontwikkeling %' },
      { k: 'wia_bodem_pct', label: 'WIA-bodem %' },
      { k: 'wga_hiaat_pct', label: 'WGA-hiaat %' },
    ],
  },
  {
    titel: 'Pensioen',
    velden: [
      { k: 'pensioen_wg_pct', label: 'Pensioen werkgever %' },
      { k: 'pensioen_franchise_maand', label: 'Franchise per maand (€)' },
      { k: 'pensioen_toetredingsleeftijd', label: 'Toetredingsleeftijd', step: '1' },
    ],
  },
  {
    titel: 'Normen',
    velden: [
      { k: 'normuren_maand', label: 'Normuren per maand' },
      { k: 'verlofdagen_fulltime', label: 'Verlofdagen fulltime' },
      { k: 'werkdagen_jaar', label: 'Werkdagen per jaar' },
    ],
  },
];

function LoonkostenTarievenTab({ onFeedback }) {
  const nextYear = new Date().getFullYear() + 1;
  const yearOptions = [];
  for (let y = 2026; y <= nextYear + 4; y++) yearOptions.push(y);
  const [year, setYear] = useState(new Date().getFullYear());
  const [tarieven, setTarieven] = useState(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [years, setYears] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: list }] = await Promise.all([
        api.get(`/loonkosten-tarieven/${year}`),
        api.get('/loonkosten-tarieven'),
      ]);
      setTarieven(d.tarieven);
      setExists(d.exists);
      setYears(list.years || []);
    } catch (e) {
      onFeedback({ type: 'error', msg: 'Kon loonkosten-tarieven niet laden' });
    } finally {
      setLoading(false);
    }
  }, [year, onFeedback]);

  useEffect(() => { load(); }, [load]);

  function setField(k, v) {
    setTarieven((prev) => ({ ...prev, [k]: v }));
  }

  async function copyFromPrev() {
    try {
      const { data } = await api.get(`/loonkosten-tarieven/${year - 1}`);
      setTarieven(data.tarieven);
      onFeedback({ type: 'success', msg: `Tarieven van ${year - 1} overgenomen (nog niet opgeslagen)` });
    } catch (e) {
      onFeedback({ type: 'error', msg: 'Kon vorig jaar niet ophalen' });
    }
  }

  async function save() {
    setSaving(true);
    try {
      const numeric = {};
      Object.entries(tarieven).forEach(([k, v]) => { numeric[k] = v === '' || v === null ? null : Number(v); });
      await api.put(`/loonkosten-tarieven/${year}`, { tarieven: numeric });
      onFeedback({ type: 'success', msg: `Loonkosten-tarieven ${year} opgeslagen` });
      await load();
    } catch (e) {
      onFeedback({ type: 'error', msg: e.response?.data?.detail || 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tab-content-loonkosten-tarieven">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Gauge size={18} /> Loonkosten-tarieven
        </h3>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500">Jaar</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="tarieven-year-select"
            className="rounded-md border border-slate-200 px-3 py-1.5 text-sm"
          >
            {yearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>
        </div>
      </div>

      <p className="text-sm text-slate-500 mb-4">
        Tarieven voor de integrale werkgeverskosten per uur (loonkosten-per-uur). Dit is een
        aparte kosten-/rapportagelaag en raakt de salarisberekening niet aan. 2026 is vooraf ingevuld.
      </p>

      {loading || !tarieven ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="animate-spin" size={16} /> Laden…</div>
      ) : (
        <>
          <div className="mb-3">
            {exists ? (
              <span data-testid="tarieven-status" className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">Vastgelegd</span>
            ) : (
              <span data-testid="tarieven-status" className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">Nieuw (nog niet opgeslagen)</span>
            )}
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {TARIEF_GROEPEN.map((groep) => (
              <div key={groep.titel}>
                <h4 className="text-sm font-semibold text-brand-navy mb-2">{groep.titel}</h4>
                <div className="space-y-2">
                  {groep.velden.map((v) => (
                    <div key={v.k} className="flex items-center justify-between gap-3">
                      <label className="text-sm text-slate-600">{v.label}</label>
                      <input
                        type="number"
                        step={v.step || '0.001'}
                        min="0"
                        value={tarieven[v.k] ?? ''}
                        onChange={(e) => setField(v.k, e.target.value)}
                        data-testid={`tarief-${v.k}`}
                        className="w-32 rounded-md border border-slate-200 px-2 py-1 text-sm text-right"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-2">
            <button
              onClick={copyFromPrev}
              data-testid="tarieven-copy-prev-btn"
              className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Kopieer van {year - 1}
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="tarieven-save-btn"
              className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-50"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Opslaan
            </button>
          </div>

          {years.length > 0 && (
            <div className="mt-6 border-t border-slate-100 pt-3 text-xs text-slate-400">
              Vastgelegde jaren: {years.map((y) => y.loon_year).join(' · ')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PkDoelTab({ onFeedback }) {
  const [config, setConfig] = useState(null); // {entries, active_ingangsdatum, today}
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // New-entry form
  const [ingangsdatum, setIngangsdatum] = useState('');
  const [defaultPct, setDefaultPct] = useState('32.5');
  const [topPct, setTopPct] = useState('27.5');
  const [kritiekPct, setKritiekPct] = useState('37.5');
  const [rows, setRows] = useState([]); // [{slug, pct}]
  const [addSlug, setAddSlug] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get('/loonkosten/pk-doel'), api.get('/locations')])
      .then(([{ data: d }, { data: locs }]) => {
        setConfig(d);
        setLocations(locs || []);
      })
      .catch(() => onFeedback({ type: 'error', msg: 'Kon PK%-doel niet laden' }))
      .finally(() => setLoading(false));
  }, [onFeedback]);
  useEffect(() => { load(); }, [load]);

  const nameFor = (slug) => locations.find((l) => l.slug === slug)?.name || slug;
  const availableLocs = locations.filter((l) => !rows.some((r) => r.slug === l.slug));
  const today = config?.today || new Date().toISOString().slice(0, 10);

  function addOverride() {
    if (!addSlug) return;
    setRows((prev) => [...prev, { slug: addSlug, pct: defaultPct || '32.5' }]);
    setAddSlug('');
  }
  function removeOverride(slug) { setRows((prev) => prev.filter((r) => r.slug !== slug)); }

  async function save() {
    setSaving(true);
    try {
      const per_location = {};
      rows.forEach((r) => { if (r.pct !== '' && r.pct != null) per_location[r.slug] = Number(r.pct); });
      await api.put('/loonkosten/pk-doel', {
        ingangsdatum: ingangsdatum || today,
        default_pct: Number(defaultPct), per_location,
        top_pct: Number(topPct), kritiek_pct: Number(kritiekPct),
      });
      onFeedback({ type: 'success', msg: `Norm opgeslagen per ${ingangsdatum || today}` });
      setIngangsdatum(''); setRows([]);
      load();
    } catch (e) {
      onFeedback({ type: 'error', msg: e.response?.data?.detail || 'Opslaan mislukt' });
    } finally { setSaving(false); }
  }

  async function deleteEntry(dt) {
    try {
      await api.delete(`/loonkosten/pk-doel/${dt}`);
      onFeedback({ type: 'success', msg: `Toekomstige norm per ${dt} verwijderd` });
      load();
    } catch (e) {
      onFeedback({ type: 'error', msg: e.response?.data?.detail || 'Verwijderen mislukt' });
    }
  }

  const entries = config?.entries || [];
  const overridesLabel = (e) => {
    const keys = Object.keys(e.per_location || {});
    return keys.length ? keys.map((s) => `${nameFor(s)} ${e.per_location[s]}%`).join(', ') : '—';
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tab-content-pk-doel">
      <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-2">
        <Gauge size={18} /> PK%-doel (norm-historie)
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Het doel-PK% (loonkosten ÷ omzet) bepaalt de 4-traps kleuren (goud/groen/oranje/rood).
        Een normwijziging geldt <strong>vanaf haar ingangsdatum</strong>; oude weken behouden de norm
        die tóén gold. Historische normen zijn vastgelegd; alleen toekomstige zijn te verwijderen.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="animate-spin" size={16} /> Laden…</div>
      ) : (
        <>
          {/* History table */}
          <div className="overflow-hidden rounded-lg border border-slate-100 mb-6" data-testid="pk-doel-history">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Ingangsdatum</th>
                  <th className="px-3 py-2 text-right font-medium">Top / Norm / Kritiek</th>
                  <th className="px-3 py-2 text-left font-medium">Afwijkingen</th>
                  <th className="px-3 py-2 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const isActive = e.ingangsdatum === config.active_ingangsdatum;
                  const isFuture = e.ingangsdatum > today;
                  return (
                    <tr key={e.ingangsdatum} className={`border-t border-slate-100 ${isActive ? 'bg-emerald-50/50' : ''}`} data-testid={`pk-doel-history-row-${e.ingangsdatum}`}>
                      <td className="px-3 py-2">
                        <span className="tabular-nums text-slate-700">{e.ingangsdatum}</span>
                        {isActive && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid="pk-doel-active-badge">NU ACTIEF</span>}
                        {isFuture && <span className="ml-2 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">Gepland</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">{e.top_pct}% / <strong>{e.default_pct}%</strong> / {e.kritiek_pct}%</td>
                      <td className="px-3 py-2 text-[12px] text-slate-500">{overridesLabel(e)}</td>
                      <td className="px-3 py-2 text-right">
                        {isFuture ? (
                          <button onClick={() => deleteEntry(e.ingangsdatum)} data-testid={`pk-doel-delete-${e.ingangsdatum}`}
                            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600" title="Toekomstige norm verwijderen">
                            <Trash2 size={15} />
                          </button>
                        ) : <span className="text-[11px] text-slate-300">vastgelegd</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* New-entry form */}
          <div className="border-t border-slate-100 pt-4">
            <p className="text-sm font-semibold text-slate-700 mb-3">Nieuwe norm toevoegen</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ingangsdatum</label>
                <input type="date" min={today} value={ingangsdatum} onChange={(e) => setIngangsdatum(e.target.value)}
                  data-testid="pk-doel-ingangsdatum" className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Topgrens (goud onder)</label>
                <input type="number" min="0" max="100" step="0.1" value={topPct} onChange={(e) => setTopPct(e.target.value)}
                  data-testid="pk-doel-top" className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Norm (bedrijfsbreed)</label>
                <input type="number" min="0" max="100" step="0.1" value={defaultPct} onChange={(e) => setDefaultPct(e.target.value)}
                  data-testid="pk-doel-default" className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Kritiekgrens (rood boven)</label>
                <input type="number" min="0" max="100" step="0.1" value={kritiekPct} onChange={(e) => setKritiekPct(e.target.value)}
                  data-testid="pk-doel-kritiek" className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right" />
              </div>
            </div>

            <p className="text-xs font-medium text-slate-600 mb-2">Afwijkingen per locatie</p>
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.slug} className="flex items-center gap-3" data-testid={`pk-doel-row-${r.slug}`}>
                  <span className="text-sm text-slate-700 w-48">{nameFor(r.slug)}</span>
                  <div className="flex items-center gap-1">
                    <input type="number" min="0" max="100" step="0.1" value={r.pct}
                      onChange={(e) => setRows((prev) => prev.map((x) => x.slug === r.slug ? { ...x, pct: e.target.value } : x))}
                      data-testid={`pk-doel-pct-${r.slug}`} className="w-28 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right" />
                    <span className="text-slate-500 text-sm">%</span>
                  </div>
                  <button onClick={() => removeOverride(r.slug)} data-testid={`pk-doel-remove-${r.slug}`}
                    className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            {availableLocs.length > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <select value={addSlug} onChange={(e) => setAddSlug(e.target.value)} data-testid="pk-doel-add-select"
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                  <option value="">Locatie toevoegen…</option>
                  {availableLocs.map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
                </select>
                <button onClick={addOverride} disabled={!addSlug} data-testid="pk-doel-add-btn"
                  className="flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  <Plus size={14} /> Toevoegen
                </button>
              </div>
            )}

            <div className="mt-5">
              <button onClick={save} disabled={saving} data-testid="pk-doel-save-btn"
                className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-50">
                {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Norm opslaan
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DailyOpsTab({ onFeedback }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/dailyops/status')
      .then(({ data }) => setStatus(data))
      .catch(() => onFeedback({ type: 'error', msg: 'Kon DailyOps-status niet laden' }))
      .finally(() => setLoading(false));
  }, [onFeedback]);

  useEffect(() => { load(); }, [load]);

  async function testConnection() {
    setTesting(true);
    try {
      const { data } = await api.post('/dailyops/test');
      onFeedback({
        type: data.ok ? 'success' : 'error',
        msg: data.ok
          ? `Verbinding OK — ${data.sample_location_count} locaties opgehaald`
          : `Verbinding mislukt: ${data.error || data.source}`,
      });
      load();
    } catch (e) {
      onFeedback({ type: 'error', msg: e.response?.data?.detail || 'Test mislukt' });
    } finally {
      setTesting(false);
    }
  }

  const sync = status?.last_sync || {};
  const match = status?.match || {};
  const syncTone = sync.source === 'live' ? 'bg-emerald-100 text-emerald-700'
    : sync.source === 'cache' || sync.source === 'stale' ? 'bg-amber-100 text-amber-800'
    : 'bg-rose-100 text-rose-700';
  const syncLabel = { live: 'Live verbonden', cache: 'Uit cache', stale: 'Cache (DailyOps niet bereikbaar)', unavailable: 'Niet bereikbaar' }[sync.source] || sync.source;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tab-content-dailyops">
      <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-2">
        <Gauge size={18} /> DailyOps-koppeling
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        SmartRoster leest DailyOps (daily-ops.nl) alleen-lezen uit voor de echte targets, omzet en
        PK-norm. De verwachte omzet in de planner/cockpit wordt hiermee voorgevuld (handmatige invoer
        blijft leidend). De sleutel staat veilig in de server en wordt nooit getoond.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="animate-spin" size={16} /> Laden…</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-100 p-3">
              <div className="text-xs text-slate-500">Verbindingsstatus</div>
              <span className={`mt-1 inline-block rounded-full px-2.5 py-1 text-xs font-medium ${syncTone}`} data-testid="dailyops-sync-status">
                {syncLabel}
              </span>
              {sync.fetched_at && (
                <div className="mt-1 text-[11px] text-slate-400">
                  Laatste sync: {new Date(sync.fetched_at).toLocaleString('nl-NL')}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-slate-100 p-3">
              <div className="text-xs text-slate-500">Base-URL</div>
              <div className="mt-1 text-sm text-slate-700" data-testid="dailyops-base-url">{status?.base_url || '—'}</div>
              <div className="text-xs text-slate-500 mt-2">API-sleutel (gemaskeerd)</div>
              <div className="mt-1 text-sm text-slate-700 font-mono" data-testid="dailyops-key-masked">{status?.key_masked || '—'}</div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-100 p-3">
            <div className="text-xs text-slate-500 mb-1">Gematchte truckwash-locaties</div>
            <div className="text-sm text-slate-700" data-testid="dailyops-match-count">
              {match.matched_count || 0} van {match.dailyops_truckwash_count || 0} DailyOps-truckwash-locaties gekoppeld.
            </div>
            {match.unmatched_dailyops && match.unmatched_dailyops.length > 0 && (
              <div className="mt-1 text-[12px] text-amber-700" data-testid="dailyops-unmatched">
                Niet gematcht: {match.unmatched_dailyops.join(', ')}
              </div>
            )}
          </div>

          <button onClick={testConnection} disabled={testing} data-testid="dailyops-test-btn"
            className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-50">
            {testing ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Test verbinding
          </button>
        </div>
      )}
    </div>
  );
}

function ContractMeldingTab({ onFeedback }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recipientsCfg, setRecipientsCfg] = useState(null);
  const [savingRecipients, setSavingRecipients] = useState(false);

  useEffect(() => {
    api.get('/system-config/contract_alerts')
      .then((r) => setConfig(r.data.value))
      .catch(() => setConfig({ horizon_days: 30, frequentie: 'dagelijks' }))
      .finally(() => setLoading(false));
    api.get('/system-config/location_notification_recipients')
      .then((r) => setRecipientsCfg(r.data.value))
      .catch(() => setRecipientsCfg({ include_owner_admin: false }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/system-config/contract_alerts', { value: config });
      setConfig(data.value);
      onFeedback({ type: 'success', msg: 'Contract-melding opgeslagen' });
    } catch (e) {
      onFeedback({ type: 'error', msg: e?.response?.data?.detail || 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  };

  const saveRecipients = async (nextVal) => {
    setSavingRecipients(true);
    try {
      const { data } = await api.put('/system-config/location_notification_recipients', { value: nextVal });
      setRecipientsCfg(data.value);
      onFeedback({ type: 'success', msg: 'Ontvangerfilter opgeslagen' });
    } catch (e) {
      onFeedback({ type: 'error', msg: e?.response?.data?.detail || 'Opslaan mislukt' });
    } finally {
      setSavingRecipients(false);
    }
  };

  if (loading || !config) return <p className="text-sm text-slate-500">Laden…</p>;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tab-contractmelding-content">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">Contract-afloop-melding</h3>
        <button
          onClick={save}
          disabled={saving}
          data-testid="btn-save-contractmelding"
          className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
        >
          {saving ? 'Opslaan…' : 'Opslaan'}
        </button>
      </div>
      <p className="mb-4 text-sm text-slate-600">
        In-app melding voor owner/admin met verlopen en binnenkort aflopende contracten. Er wordt geen e-mail verstuurd.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-slate-600">Horizon (dagen, 1-120)</label>
          <input
            type="number" min="1" max="120" step="1"
            value={config.horizon_days}
            onChange={(e) => setConfig({ ...config, horizon_days: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
            data-testid="contractmelding-horizon"
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">Frequentie</label>
          <select
            value={config.frequentie}
            onChange={(e) => setConfig({ ...config, frequentie: e.target.value })}
            data-testid="contractmelding-frequentie"
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="dagelijks">Dagelijks</option>
            <option value="maandag">Alleen maandag</option>
            <option value="uit">Uit</option>
          </select>
        </div>
      </div>

      {recipientsCfg && (
        <div className="mt-6 border-t border-slate-200 pt-4" data-testid="ontvangerfilter-block">
          <h4 className="text-sm font-semibold text-slate-900">Ontvangers van locatiemeldingen</h4>
          <p className="mt-0.5 text-xs text-slate-500">
            Geldt voor ziekmeldingen, ondertekende documenten en de wekelijkse rooster-weekcheck.
            Standaard krijgen alleen de bedrijfsleiders/managers van de betreffende locatie een melding.
          </p>
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
            <Switch
              checked={!!recipientsCfg.include_owner_admin}
              disabled={savingRecipients}
              onCheckedChange={(v) => saveRecipients({ ...recipientsCfg, include_owner_admin: v })}
              data-testid="ontvangerfilter-toggle"
            />
            Ook owner/admin meenemen
          </label>
        </div>
      )}
      <MeldingenHistorieList />
    </div>
  );
}

function MeldingenHistorieList() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get('/notifications/contract-afloop-recent')
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <div className="mt-6 border-t border-slate-200 pt-4" data-testid="contractmelding-historie">
      <h4 className="text-sm font-semibold text-slate-900">Laatste meldingen</h4>
      <p className="mt-0.5 text-xs text-slate-500">Alleen-lezen — de laatste 20 verstuurde contract-afloopmeldingen, nieuwste eerst.</p>
      {items === null ? (
        <p className="mt-3 text-sm text-slate-500">Laden…</p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500" data-testid="contractmelding-historie-empty">Nog geen meldingen verstuurd.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((it, i) => (
            <li key={i} className="rounded-md border border-slate-200 px-3 py-2 text-xs" data-testid={`contractmelding-historie-item-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-900">{it.title}</span>
                <span className="text-slate-400">
                  {it.created_at ? new Date(it.created_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                </span>
              </div>
              <p className="mt-0.5 text-slate-600">{it.body}</p>
              <p className="mt-0.5 text-slate-400">aan {it.aan}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function InklokkenTab({ onFeedback }) {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/system-config/qr_only_clock')
      .then(({ data }) => setEnabled(data?.value?.enabled !== false))
      .catch(() => onFeedback({ type: 'error', msg: 'Kon inklok-instelling niet laden' }))
      .finally(() => setLoading(false));
  }, [onFeedback]);

  async function toggle(next) {
    setSaving(true);
    try {
      await api.put('/system-config/qr_only_clock', { value: { enabled: next } });
      setEnabled(next);
      onFeedback({ type: 'success', msg: next ? 'QR-only inklokken ingeschakeld' : 'QR-only inklokken uitgeschakeld' });
    } catch (e) {
      onFeedback({ type: 'error', msg: e.response?.data?.detail || 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tab-content-inklokken">
      <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-2">
        <QrCode size={18} /> Inklokken — QR-only (fraude-preventie)
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Wanneer ingeschakeld kunnen medewerkers uitsluitend inklokken met hun eigen persoonlijke
        QR-code (zichtbaar in het Medewerkersportaal onder &ldquo;QR&rdquo;). De lijst/tik-methode op
        het inklokstation wordt volledig uitgeschakeld — dit voorkomt dat iemand een collega inklokt.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="animate-spin" size={16} /> Laden…</div>
      ) : (
        <div className="flex items-center justify-between rounded-lg border border-slate-100 p-4">
          <div>
            <div className="text-sm font-medium text-slate-900">QR-only inklokken</div>
            <div className="text-xs text-slate-500">
              {enabled ? 'Alleen inklokken via persoonlijke QR-code.' : 'Lijst/tik-inklokken is toegestaan.'}
            </div>
          </div>
          <Switch
            checked={enabled}
            disabled={saving}
            onCheckedChange={toggle}
            data-testid="qr-only-toggle"
          />
        </div>
      )}
    </div>
  );
}


export default function Instellingen() {
  const { user, refreshMe } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'gebruikers';
  const [tab, setTab] = useState(initialTab);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [accountForm, setAccountForm] = useState({ name: user?.name || '', password: '' });
  const [feedback, setFeedback] = useState({ type: '', msg: '' });

  async function loadUsers() {
    setLoadingUsers(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (e) {
      setFeedback({ type: 'error', msg: 'Kon gebruikers niet laden' });
    } finally {
      setLoadingUsers(false);
    }
  }

  async function loadLocations() {
    try {
      const { data } = await api.get('/locations');
      setLocations(data);
    } catch (e) {
      console.error('[instellingen] load locations failed', e);
    }
  }

  useEffect(() => {
    if (tab === 'gebruikers') loadUsers();
    if (tab === 'locaties') loadLocations();
  }, [tab]);

  useEffect(() => {
    setAccountForm({ name: user?.name || '', password: '' });
  }, [user]);

  async function handleCreateUser(form) {
    try {
      await api.post('/auth/register', form);
      setCreatingUser(false);
      setFeedback({ type: 'success', msg: 'Gebruiker aangemaakt' });
      loadUsers();
    } catch (e) {
      setFeedback({ type: 'error', msg: e.response?.data?.detail || 'Aanmaken mislukt' });
    }
  }

  async function handleUpdateUser(u, form) {
    try {
      const { headers } = await api.get(`/users/${u.id}`);
      const etag = headers.etag;
      await api.put(`/users/${u.id}`, form, { headers: { 'If-Match': etag } });
      setEditingUser(null);
      setFeedback({ type: 'success', msg: 'Gebruiker bijgewerkt' });
      loadUsers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Bijwerken mislukt';
      setFeedback({ type: 'error', msg });
    }
  }

  async function handleDeleteUser(u) {
    if (!window.confirm(`Weet je zeker dat je ${u.name} wilt verwijderen?`)) return;
    try {
      const { headers } = await api.get(`/users/${u.id}`);
      await api.delete(`/users/${u.id}`, { headers: { 'If-Match': headers.etag } });
      setFeedback({ type: 'success', msg: 'Gebruiker verwijderd' });
      loadUsers();
    } catch (e) {
      setFeedback({ type: 'error', msg: e.response?.data?.detail || 'Verwijderen mislukt' });
    }
  }

  async function handleAccountUpdate() {
    try {
      const { headers } = await api.get(`/users/${user.id}`);
      const payload = { name: accountForm.name };
      if (accountForm.password) payload.password = accountForm.password;
      await api.put(`/users/${user.id}`, payload, { headers: { 'If-Match': headers.etag } });
      setAccountForm({ ...accountForm, password: '' });
      setFeedback({ type: 'success', msg: 'Account bijgewerkt' });
      refreshMe();
    } catch (e) {
      setFeedback({ type: 'error', msg: 'Bijwerken mislukt' });
    }
  }

  return (
    <div className="space-y-4" data-testid="page-instellingen">
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {[
          { id: 'gebruikers', label: 'Gebruikers' },
          { id: 'locaties', label: 'Locaties' },
          { id: 'landingscope', label: 'Landingslocatie', ownerAdmin: true },
          { id: 'urenguard', label: 'Uren-guard', ownerAdmin: true },
          { id: 'pk-doel', label: 'PK%-doel', ownerAdminOM: true },
          { id: 'dailyops', label: 'DailyOps-koppeling', ownerAdmin: true },
          { id: 'inklokken', label: 'Inklokken', ownerAdmin: true },
          { id: 'contractmelding', label: 'Contract-melding', ownerAdmin: true },
          { id: 'feestdagen', label: 'Feestdagen' },
          { id: 'documenten', label: 'Documenten' },
          { id: 'instructies', label: 'Instructies' },
          { id: 'salarisexport', label: 'Salarisexport', ownerAdmin: true },
          { id: 'loonperiode-kalender', label: 'Loonperiode-kalender', ownerAdmin: true },
          { id: 'loonkosten-tarieven', label: 'Loonkosten-tarieven', ownerAdmin: true },
          { id: 'bedrijfsgegevens', label: 'Bedrijfsgegevens', ownerOnly: true },
          { id: 'data-privacy', label: 'Data & Privacy', ownerOnly: true },
          { id: 'ai', label: 'AI' },
          { id: 'account', label: 'Mijn Account' },
        ].filter((t) => (!t.ownerOnly || user?.role === 'owner') && (!t.ownerAdmin || user?.role === 'owner' || user?.role === 'admin') && (!t.ownerAdminOM || ['owner', 'admin', 'multi_location'].includes(user?.role))).map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setTab(t.id);
              setSearchParams(t.id === 'gebruikers' ? {} : { tab: t.id });
            }}
            data-testid={`tab-${t.id}`}
            className={`-mb-px px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.id
                ? 'border-brand-amber text-brand-navy'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'feestdagen' && <FeestdagenTab onFeedback={setFeedback} />}
      {tab === 'documenten' && <DocumentenCentraalTab onFeedback={setFeedback} />}
      {tab === 'instructies' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tab-instructies-content">
          <InstructiebladenSection isManagerOnly={!['owner', 'admin'].includes(user?.role)} />
        </div>
      )}
      {tab === 'bedrijfsgegevens' && <BedrijfsgegevensTab onFeedback={setFeedback} />}
      {tab === 'data-privacy' && <DataPrivacyTab onFeedback={setFeedback} />}
      {tab === 'salarisexport' && <SalarisexportTab onFeedback={setFeedback} />}
      {tab === 'loonperiode-kalender' && <LoonperiodeKalenderTab onFeedback={setFeedback} />}
      {tab === 'loonkosten-tarieven' && <LoonkostenTarievenTab onFeedback={setFeedback} />}
      {tab === 'pk-doel' && <PkDoelTab onFeedback={setFeedback} />}
      {tab === 'dailyops' && <DailyOpsTab onFeedback={setFeedback} />}
      {tab === 'inklokken' && <InklokkenTab onFeedback={setFeedback} />}
      {tab === 'contractmelding' && <ContractMeldingTab onFeedback={setFeedback} />}

      {feedback.msg && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            feedback.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}
          data-testid="feedback-banner"
        >
          {feedback.msg}
        </div>
      )}

      {tab === 'landingscope' && <LandingScopeTab onFeedback={setFeedback} />}
      {tab === 'urenguard' && <UrenGuardTab onFeedback={setFeedback} />}
      {tab === 'gebruikers' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <UsersIcon size={18} /> Gebruikers
            </h3>
            <button
              onClick={() => setCreatingUser(true)}
              data-testid="btn-create-user"
              className="flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light"
            >
              <Plus size={14} /> Nieuwe gebruiker
            </button>
          </div>
          {loadingUsers ? (
            <p className="text-sm text-slate-500">Laden…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Naam</th>
                    <th className="py-2 pr-3">E-mail</th>
                    <th className="py-2 pr-3">Rol</th>
                    <th className="py-2 pr-3">Locaties</th>
                    <th className="py-2 pr-3 text-right">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-medium text-slate-900">{u.name}</td>
                      <td className="py-2 pr-3 text-slate-700">{u.email}</td>
                      <td className="py-2 pr-3">
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{ROLE_LABELS[u.role] || u.role}</span>
                      </td>
                      <td className="py-2 pr-3 text-slate-600 text-xs">{(u.locations || []).join(', ') || '—'}</td>
                      <td className="py-2 pr-3 text-right">
                        <button onClick={() => setEditingUser(u)} className="mr-1 inline-flex rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-navy" data-testid={`btn-edit-user-${u.email}`}>
                          <Pencil size={14} />
                        </button>
                        {u.id !== user?.id && (
                          <button onClick={() => handleDeleteUser(u)} className="inline-flex rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid={`btn-delete-user-${u.email}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'locaties' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <Building2 size={18} /> Locaties ({locations.length})
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {locations.map((loc) => (
              <div key={loc.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-slate-900">{loc.name}</p>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-wide">{loc.type}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">slug: {loc.slug}</p>
                {loc.type === 'truckwash' && (
                  <p className="mt-1 text-xs text-slate-600">
                    {loc.banen} banen · DE {loc.de_impact} · BE {loc.be_impact}
                    {loc.port_dependent && ' · Haven'}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'ai' && <AIInstellingen locations={locations} loadLocations={loadLocations} />}

      {tab === 'account' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm max-w-md">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Mijn Account</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Naam</label>
              <input
                value={accountForm.name}
                onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                data-testid="account-name"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-mail</label>
              <input value={user?.email || ''} disabled className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" />
              <p className="mt-1 text-xs text-slate-500">E-mail kan alleen door owner/admin gewijzigd worden via gebruikersbeheer.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nieuw wachtwoord (laat leeg om niet te wijzigen)</label>
              <input
                type="password"
                value={accountForm.password}
                onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })}
                data-testid="account-password"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <button
              onClick={handleAccountUpdate}
              data-testid="account-save"
              className="flex items-center gap-2 rounded-md bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy-light"
            >
              <Save size={14} /> Opslaan
            </button>
          </div>
        </div>
      )}

      <Modal open={creatingUser} onClose={() => setCreatingUser(false)} title="Nieuwe gebruiker">
        <UserForm
          locations={locations.length ? locations : []}
          onSubmit={handleCreateUser}
          onCancel={() => setCreatingUser(false)}
          loadLocations={loadLocations}
        />
      </Modal>

      <Modal open={!!editingUser} onClose={() => setEditingUser(null)} title="Gebruiker bewerken">
        {editingUser && (
          <UserForm
            initial={editingUser}
            locations={locations.length ? locations : []}
            onSubmit={(form) => handleUpdateUser(editingUser, form)}
            onCancel={() => setEditingUser(null)}
            loadLocations={loadLocations}
            editing
          />
        )}
      </Modal>
    </div>
  );
}

function UserForm({ initial, locations, onSubmit, onCancel, loadLocations, editing }) {
  const [form, setForm] = useState({
    email: initial?.email || '',
    name: initial?.name || '',
    password: '',
    role: initial?.role || 'single_location',
    locations: initial?.locations || [],
  });

  useEffect(() => {
    if (locations.length === 0 && loadLocations) loadLocations();
  }, [locations.length, loadLocations]);

  function toggleLoc(slug) {
    setForm((f) => ({
      ...f,
      locations: f.locations.includes(slug) ? f.locations.filter((s) => s !== slug) : [...f.locations, slug],
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const payload = { ...form };
    if (editing && !payload.password) delete payload.password;
    if (editing) delete payload.email;
    onSubmit(payload);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="user-form">
      {!editing && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">E-mail</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="userform-email"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Naam</label>
        <input
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="userform-name"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">{editing ? 'Nieuw wachtwoord (optioneel)' : 'Wachtwoord'}</label>
        <input
          type="password"
          required={!editing}
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          data-testid="userform-password"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Rol</label>
        <select
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          data-testid="userform-role"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="owner">Owner</option>
          <option value="admin">Admin</option>
          <option value="multi_location">Multi-location</option>
          <option value="single_location">Bedrijfsleider</option>
        </select>
      </div>
      {(form.role === 'multi_location' || form.role === 'single_location') && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Locaties</label>
          <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 p-2 grid grid-cols-2 gap-1">
            {locations.map((l) => (
              <label key={l.slug} className="flex items-center gap-1 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.locations.includes(l.slug)}
                  onChange={() => toggleLoc(l.slug)}
                  data-testid={`userform-loc-${l.slug}`}
                />
                {l.name}
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
          Annuleren
        </button>
        <button type="submit" data-testid="userform-submit" className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light">
          {editing ? 'Opslaan' : 'Aanmaken'}
        </button>
      </div>
    </form>
  );
}



function AIInstellingen({ locations, loadLocations }) {
  const [editing, setEditing] = useState({});
  const [feedback, setFeedback] = useState({ type: '', msg: '' });

  useEffect(() => {
    if (locations.length === 0 && loadLocations) loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveCap(loc, newCap) {
    setFeedback({ type: '', msg: '' });
    try {
      const { headers } = await api.get(`/locations/${loc.slug}`);
      await api.put(
        `/locations/${loc.slug}`,
        { ai_monthly_cap_eur: parseFloat(newCap) },
        { headers: { 'If-Match': headers.etag } },
      );
      setFeedback({ type: 'success', msg: `Cap voor ${loc.name} bijgewerkt naar €${newCap}` });
      setEditing((e) => ({ ...e, [loc.slug]: undefined }));
      loadLocations();
    } catch (e) {
      setFeedback({ type: 'error', msg: 'Bijwerken mislukt' });
    }
  }

  const aiLocs = locations.filter((l) => l.has_ai);

  return (
    <div className="space-y-4" data-testid="ai-instellingen">
      <div className="rounded-xl bg-brand-amber/10 border border-brand-amber/30 p-4 text-xs text-slate-800">
        <p className="font-semibold mb-1">AI-bescherming</p>
        <p>Voor elke locatie met AI: max 5 generate-week calls per dag + maandelijkse budget-cap (default €50). Bij 80% van de cap krijgt de bedrijfsleider een UI-waarschuwing; bij 100% wordt AI tijdelijk uitgeschakeld en kan de fallback van het standaardrooster gebruikt worden.</p>
      </div>

      {feedback.msg && (
        <div className={`rounded-md px-3 py-2 text-sm ${feedback.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{feedback.msg}</div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Maandelijkse budget-caps per locatie</h3>
        <div className="space-y-2">
          {aiLocs.map((loc) => (
            <div key={loc.slug} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid={`ai-loc-${loc.slug}`}>
              <div>
                <p className="text-sm font-semibold text-slate-900">{loc.name}</p>
                <p className="text-xs text-slate-500">slug: {loc.slug}</p>
              </div>
              {editing[loc.slug] !== undefined ? (
                <div className="flex items-center gap-1">
                  <span className="text-sm">€</span>
                  <input
                    type="number"
                    step="5"
                    min="0"
                    value={editing[loc.slug]}
                    onChange={(e) => setEditing({ ...editing, [loc.slug]: e.target.value })}
                    data-testid={`ai-cap-input-${loc.slug}`}
                    className="w-20 rounded-md border border-slate-300 px-2 py-1 text-sm tabular-nums"
                  />
                  <button onClick={() => saveCap(loc, editing[loc.slug])} data-testid={`ai-cap-save-${loc.slug}`}
                    className="rounded bg-brand-navy px-2 py-1 text-xs text-white">OK</button>
                  <button onClick={() => setEditing({ ...editing, [loc.slug]: undefined })}
                    className="rounded border border-slate-300 px-2 py-1 text-xs">×</button>
                </div>
              ) : (
                <button
                  onClick={() => setEditing({ ...editing, [loc.slug]: loc.ai_monthly_cap_eur || 50 })}
                  data-testid={`ai-cap-edit-${loc.slug}`}
                  className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 tabular-nums"
                >
                  €{loc.ai_monthly_cap_eur || 50}/maand
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function SalarisexportTab({ onFeedback }) {
  const [config, setConfig] = useState(null); // full salaris_export value (preserved on save)
  const [locations, setLocations] = useState([]);
  const [codes, setCodes] = useState({}); // editable werkgever_codes map: slug -> code
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [cfgRes, locRes] = await Promise.all([
          api.get('/system-config/salaris_export'),
          api.get('/locations'),
        ]);
        if (cancelled) return;
        const value = cfgRes.data?.value || {};
        setConfig(value);
        setCodes({ ...(value.werkgever_codes || {}) });
        setLocations((locRes.data || []).slice().sort((a, b) => a.name.localeCompare(b.name, 'nl')));
      } catch (e) {
        if (!cancelled) onFeedback?.({ type: 'error', msg: 'Kon salarisexport-configuratie niet laden' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onFeedback]);

  const setCode = (slug, v) => setCodes((c) => ({ ...c, [slug]: v }));

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      // Normalise: trim + drop empties so a cleared field doesn't store "".
      const cleaned = {};
      Object.entries(codes).forEach(([slug, v]) => {
        const t = (v ?? '').toString().trim();
        if (t) cleaned[slug] = t;
      });
      // Preserve all other keys (lonerssoort/status/looncomponent/dagensoort).
      const newValue = { ...config, werkgever_codes: cleaned };
      await api.put('/system-config/salaris_export', { value: newValue });
      setConfig(newValue);
      setCodes({ ...cleaned });
      onFeedback?.({ type: 'success', msg: 'Werkgevercodes opgeslagen' });
    } catch (e) {
      onFeedback?.({ type: 'error', msg: e?.response?.data?.detail || 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Laden…
      </div>
    );
  }

  const missingCount = locations.filter((l) => !(codes[l.slug] || '').toString().trim()).length;
  const lc = config.looncomponent || {};
  const ds = config.dagensoort || {};

  return (
    <div className="space-y-4" data-testid="tab-salarisexport-content">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <FileText size={18} /> Werkgevercodes per locatie
          </h3>
          <button
            onClick={save}
            disabled={saving}
            data-testid="salarisexport-save"
            className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          De werkgevercode wordt per locatie meegestuurd in de <em>mutaties TW</em>-export naar Exact.
          {missingCount > 0 ? (
            <span className="ml-1 font-medium text-amber-700" data-testid="salarisexport-missing-count">
              {missingCount} locatie(s) zonder code.
            </span>
          ) : (
            <span className="ml-1 font-medium text-emerald-700">Alle locaties hebben een code.</span>
          )}
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-3">Locatie</th>
                <th className="py-2 pr-3">Slug</th>
                <th className="py-2 pr-3">Werkgevercode</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((loc) => {
                const code = codes[loc.slug] || '';
                const missing = !code.toString().trim();
                return (
                  <tr key={loc.slug} data-testid={`werkgever-row-${loc.slug}`} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium text-slate-900">{loc.name}</td>
                    <td className="py-2 pr-3 text-xs text-slate-500">{loc.slug}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <input
                          value={code}
                          onChange={(e) => setCode(loc.slug, e.target.value)}
                          data-testid={`werkgever-code-${loc.slug}`}
                          placeholder="bijv. 6056240"
                          className={`w-40 rounded-md border px-2.5 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-brand-amber/40 ${
                            missing ? 'border-amber-300 bg-amber-50' : 'border-slate-300'
                          }`}
                        />
                        {missing && (
                          <span data-testid={`werkgever-missing-${loc.slug}`} className="rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                            ontbreekt
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="salarisexport-codes-reference">
        <h4 className="text-sm font-semibold text-slate-900 mb-2">Vaste codes (alleen-lezen)</h4>
        <p className="text-xs text-slate-500 mb-3">Looncomponenten en dagensoorten zijn vast ingesteld voor de export.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Looncomponenten</p>
            <ul className="space-y-0.5 text-xs text-slate-700">
              <li>Overuren 100% — <span className="tabular-nums">{lc.t100 || '150101'}</span></li>
              <li>Overuren 125% — <span className="tabular-nums">{lc.t125 || '150102'}</span></li>
              <li>Overuren 150% — <span className="tabular-nums">{lc.t150 || '150103'}</span></li>
              <li>Reiskosten km — <span className="tabular-nums">{lc.km || '700021'}</span></li>
            </ul>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Dagensoorten</p>
            <ul className="space-y-0.5 text-xs text-slate-700">
              <li>Gewerkt — {ds.gew || 'GEW'}</li>
              <li>Ziekte — {ds.ziekte || 'ZK'}</li>
              <li>Vakantie — {ds.vakantie || 'VAK'}</li>
              <li>Onbetaald — {ds.onbetaald || 'OBV'}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeestdagenTab({ onFeedback }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [country, setCountry] = useState('NL');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: '', name: '', country: 'NL', type: 'nationaal' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/holidays', { params: { country, year } });
      setItems(data.holidays || []);
    } catch (e) {
      console.error('[instellingen] load holidays failed', e);
      onFeedback?.({ type: 'error', msg: 'Kon feestdagen niet laden' });
    } finally {
      setLoading(false);
    }
  }, [country, year, onFeedback]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.date || !form.name) return;
    setSaving(true);
    try {
      await api.post('/holidays', form);
      setShowAdd(false);
      setForm({ date: '', name: '', country, type: 'nationaal' });
      onFeedback?.({ type: 'success', msg: 'Feestdag toegevoegd' });
      await load();
    } catch (e) {
      onFeedback?.({ type: 'error', msg: e?.response?.data?.detail || 'Toevoegen mislukt' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Verwijder deze feestdag?')) return;
    try {
      await api.delete(`/holidays/${id}`);
      onFeedback?.({ type: 'success', msg: 'Verwijderd' });
      await load();
    } catch {
      onFeedback?.({ type: 'error', msg: 'Verwijderen mislukt' });
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tab-feestdagen-content">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Calendar size={18} /> Feestdagen — {country} {year}
        </h3>
        <div className="flex items-center gap-2">
          <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="feestdagen-country" className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm">
            <option value="NL">Nederland</option>
            <option value="DE">Duitsland</option>
            <option value="BE">België</option>
          </select>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="feestdagen-year" className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm">
            {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={() => { setForm({ date: `${year}-01-01`, name: '', country, type: 'nationaal' }); setShowAdd(true); }}
            data-testid="btn-add-holiday"
            className="flex items-center gap-1 rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light"
          >
            <Plus size={14} /> Toevoegen
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="mb-4 rounded-md border-2 border-brand-amber/30 bg-brand-amber/5 p-3" data-testid="feestdag-add-form">
          <div className="grid grid-cols-4 gap-2">
            <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="holiday-date" className="rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
            <input type="text" placeholder="Naam" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="holiday-name" className="rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
            <input type="text" placeholder="Type" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} data-testid="holiday-type" className="rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
            <div className="flex gap-1">
              <button onClick={save} disabled={saving} data-testid="holiday-save" className="flex-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:opacity-50">
                {saving ? <Loader2 size={12} className="animate-spin inline" /> : <Save size={12} className="inline mr-1" />} Opslaan
              </button>
              <button onClick={() => setShowAdd(false)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Annu</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" /> Laden…</p>
      ) : items.length === 0 ? (
        <p className="py-4 text-sm text-slate-500 text-center">Geen feestdagen voor {country} {year}.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2 pr-3">Datum</th>
                <th className="py-2 pr-3">Naam</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody>
              {items.map((h) => (
                <tr key={h.id} className="border-b border-slate-100" data-testid={`holiday-row-${h.id}`}>
                  <td className="py-2 pr-3 tabular-nums text-slate-700">{h.date}</td>
                  <td className="py-2 pr-3 font-medium text-slate-900">{h.name}</td>
                  <td className="py-2 pr-3"><span className="rounded bg-slate-100 px-2 py-0.5 text-[10px]">{h.type || 'nationaal'}</span></td>
                  <td className="py-2 pr-3 text-right">
                    <button onClick={() => remove(h.id)} className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid={`btn-delete-holiday-${h.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DataPrivacyTab({ onFeedback }) {
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [anonymizing, setAnonymizing] = useState(null);

  const scan = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/employees/pii-scan');
      setCandidates(data);
    } catch (e) {
      onFeedback?.({ type: 'error', msg: e?.response?.data?.detail || 'Scan mislukt' });
    } finally {
      setLoading(false);
    }
  };

  const anonymize = async (emp) => {
    if (!window.confirm(`Definitief anonimiseren van ${emp.name} (pnr ${emp.personeelsnummer})? Dit kan niet ongedaan worden gemaakt.`)) return;
    setAnonymizing(emp.id);
    try {
      await api.post(`/employees/${emp.id}/anonymize`);
      onFeedback?.({ type: 'success', msg: `${emp.name} geanonimiseerd` });
      await scan();
    } catch (e) {
      onFeedback?.({ type: 'error', msg: e?.response?.data?.detail || 'Anonimiseren mislukt' });
    } finally {
      setAnonymizing(null);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tab-data-privacy-content">
      <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-2">
        <ShieldAlert size={18} className="text-rose-600" /> PII-scan & Data Retention
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Identificeer ex-medewerkers waarvan de fiscale bewaartermijn (7 jaar) verstreken is.
        Anonimisering verwijdert: naam, adres, e-mail, telefoon, BSN, noodcontact, geboortedatum en alle vrije notities.
        Het personeelsnummer en functionele velden (uren, rooster-historie) blijven bewaard voor analyse-doeleinden.
      </p>

      <button
        onClick={scan}
        disabled={loading}
        data-testid="btn-pii-scan"
        className="mb-4 inline-flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-50"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
        {candidates ? 'Scan opnieuw' : 'Start PII-scan'}
      </button>

      {candidates && (
        <div data-testid="pii-scan-results">
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
            <AlertTriangle size={14} />
            <span>{candidates.count} medewerker(s) uit dienst voor {candidates.threshold_date} — kandidaten voor anonimisering.</span>
          </div>
          {candidates.count === 0 ? (
            <p className="text-sm text-slate-500 py-4">Geen kandidaten. ✓</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                    <th className="py-2 pr-3">Pnr</th>
                    <th className="py-2 pr-3">Naam</th>
                    <th className="py-2 pr-3">In dienst</th>
                    <th className="py-2 pr-3">Uit dienst</th>
                    <th className="py-2 pr-3 text-right">Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.candidates.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100" data-testid={`pii-candidate-${c.personeelsnummer}`}>
                      <td className="py-2 pr-3 tabular-nums text-slate-700">{c.personeelsnummer}</td>
                      <td className="py-2 pr-3 font-medium text-slate-900">{c.name}</td>
                      <td className="py-2 pr-3 tabular-nums text-slate-600">{c.datum_in_dienst || '—'}</td>
                      <td className="py-2 pr-3 tabular-nums text-rose-600">{c.datum_uit_dienst}</td>
                      <td className="py-2 pr-3 text-right">
                        <button
                          onClick={() => anonymize(c)}
                          disabled={anonymizing === c.id}
                          data-testid={`btn-anonymize-${c.personeelsnummer}`}
                          className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-xs text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                        >
                          {anonymizing === c.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                          Anonimiseren
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function DocumentenCentraalTab({ onFeedback }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [horizonDays, setHorizonDays] = useState(30);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dispatchBusy, setDispatchBusy] = useState(false);

  const isOwnerAdmin = user?.role === 'owner' || user?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get('/documents/expiring', { params: { days: horizonDays } });
      setData(resp);
    } catch (e) {
      onFeedback?.({ type: 'error', msg: 'Documenten kon niet laden' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizonDays]);

  const filterByCat = (arr) =>
    categoryFilter === 'all' ? arr : (arr || []).filter((d) => d.category === categoryFilter);

  const buckets = data?.buckets || { expired: [], red: [], orange: [] };
  const totalShown =
    filterByCat(buckets.expired).length +
    filterByCat(buckets.red).length +
    filterByCat(buckets.orange).length;

  const dispatchNotifications = async () => {
    if (!isOwnerAdmin) return;
    if (!window.confirm(`Notificaties versturen voor ${data?.count || 0} documenten?`)) return;
    setDispatchBusy(true);
    try {
      const { data: resp } = await api.post('/documents/dispatch-expiry-notifications', null, {
        params: { days: horizonDays },
      });
      onFeedback?.({ type: 'success', msg: `${resp.sent} notificatie(s) verstuurd` });
    } catch (e) {
      onFeedback?.({ type: 'error', msg: 'Versturen mislukt' });
    } finally {
      setDispatchBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="tab-documenten-content">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <FileText size={18} /> Documenten — attentiepunten
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-slate-500">Horizon:</label>
            <select
              value={horizonDays}
              onChange={(e) => setHorizonDays(parseInt(e.target.value, 10))}
              data-testid="doc-overview-horizon"
              className="rounded-md border border-slate-200 px-2 py-1 text-sm"
            >
              <option value={7}>7 dagen</option>
              <option value={14}>14 dagen</option>
              <option value={30}>30 dagen</option>
              <option value={60}>60 dagen</option>
              <option value={90}>90 dagen</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="doc-overview-category"
              className="rounded-md border border-slate-200 px-2 py-1 text-sm"
            >
              <option value="all">Alle categorieën</option>
              {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
            {isOwnerAdmin && (
              <button
                onClick={dispatchNotifications}
                disabled={dispatchBusy || (data?.count || 0) === 0}
                data-testid="doc-overview-dispatch"
                className="flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-50"
                title="Stuur in-app meldingen aan bedrijfsleiders"
              >
                {dispatchBusy ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                Notificaties versturen
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-rose-700">Verlopen</div>
            <div className="text-2xl font-bold tabular-nums text-rose-700">
              {buckets.expired?.length || 0}
            </div>
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50/50 p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-rose-700">&lt; 7 dagen</div>
            <div className="text-2xl font-bold tabular-nums text-rose-700">
              {buckets.red?.length || 0}
            </div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-amber-800">&lt; 30 dagen</div>
            <div className="text-2xl font-bold tabular-nums text-amber-800">
              {buckets.orange?.length || 0}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Laden…
          </div>
        ) : totalShown === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="doc-overview-empty">
            <FileText size={32} className="mx-auto mb-2 text-slate-300" />
            <p>Geen documenten met attentie binnen {horizonDays} dagen. ✓</p>
          </div>
        ) : (
          <div className="space-y-4">
            <DocBucketTable
              label="Verlopen"
              tone="expired"
              items={filterByCat(buckets.expired)}
            />
            <DocBucketTable
              label="< 7 dagen"
              tone="red"
              items={filterByCat(buckets.red)}
            />
            <DocBucketTable
              label="< 30 dagen"
              tone="orange"
              items={filterByCat(buckets.orange)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function DocBucketTable({ label, tone, items }) {
  if (!items || items.length === 0) return null;
  const toneClasses = {
    expired: 'bg-rose-100 text-rose-800',
    red: 'bg-rose-50 text-rose-700',
    orange: 'bg-amber-50 text-amber-800',
  };
  return (
    <div data-testid={`doc-bucket-${tone}`}>
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-sm font-semibold text-slate-900">{label}</h4>
        <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${toneClasses[tone]}`}>
          {items.length}
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="py-2 px-3">Medewerker</th>
              <th className="py-2 px-3">Categorie</th>
              <th className="py-2 px-3">Document</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3 text-right">Actie</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`doc-overview-row-${d.id}`}>
                <td className="py-2 px-3">
                  <div className="font-medium text-slate-900">{d.employee_name || '—'}</div>
                  <div className="text-[11px] text-slate-500">
                    pnr {d.employee_personeelsnummer} · {d.location_name || d.location_slug}
                  </div>
                </td>
                <td className="py-2 px-3">
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                    {d.category_label}
                  </span>
                </td>
                <td className="py-2 px-3 text-slate-700">{d.title}</td>
                <td className="py-2 px-3">
                  <ExpiryBadge
                    status={d.expiry_status}
                    days={d.days_until_expiry}
                    expiresAt={d.expires_at}
                  />
                </td>
                <td className="py-2 px-3 text-right">
                  <Link
                    to={`/medewerkers/${d.employee_id}?tab=documenten`}
                    data-testid={`doc-overview-open-${d.id}`}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    Openen <ExternalLink size={11} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function BedrijfsgegevensTab({ onFeedback }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/company-settings').then(({ data }) => {
      if (!cancelled) {
        setForm({
          employer_name: data.employer_name || 'Truckwash 1 Group B.V.',
          employer_kvk: data.employer_kvk || '',
          employer_btw: data.employer_btw || '',
          employer_address: data.employer_address || '',
          employer_housenumber: data.employer_housenumber || '',
          employer_postcode: data.employer_postcode || '',
          employer_city: data.employer_city || 'Rucphen',
          employer_ceo_name: data.employer_ceo_name || 'Johannes Snoek',
          employer_ceo_function: data.employer_ceo_function || 'CEO Truckwash 1 Group',
        });
      }
    }).catch(() => {
      if (!cancelled) onFeedback?.({ type: 'error', msg: 'Bedrijfsgegevens konden niet laden' });
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [onFeedback]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // strip empty optional fields
      ['employer_kvk', 'employer_btw', 'employer_address', 'employer_housenumber', 'employer_postcode'].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await api.put('/company-settings', payload);
      onFeedback?.({ type: 'success', msg: 'Bedrijfsgegevens opgeslagen' });
    } catch (e) {
      onFeedback?.({ type: 'error', msg: e?.response?.data?.detail || 'Opslaan mislukt' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Laden…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tab-bedrijfsgegevens-content">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Building2 size={18} /> Bedrijfsgegevens
          </h3>
          <span className="text-[11px] text-slate-500">Worden gebruikt in contract-templates</span>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <Bg label="Bedrijfsnaam *" value={form.employer_name} onChange={(v) => update('employer_name', v)} testid="bg-name" />
          <div className="grid grid-cols-2 gap-3">
            <Bg label="KvK-nummer" value={form.employer_kvk} onChange={(v) => update('employer_kvk', v)} testid="bg-kvk" />
            <Bg label="BTW-nummer" value={form.employer_btw} onChange={(v) => update('employer_btw', v)} testid="bg-btw" />
          </div>
          <Bg label="Adres" value={form.employer_address} onChange={(v) => update('employer_address', v)} testid="bg-address" />
          <Bg label="Huisnummer" value={form.employer_housenumber} onChange={(v) => update('employer_housenumber', v)} testid="bg-housenumber" />
          <Bg label="Postcode" value={form.employer_postcode} onChange={(v) => update('employer_postcode', v)} testid="bg-postcode" />
          <Bg label="Plaats *" value={form.employer_city} onChange={(v) => update('employer_city', v)} testid="bg-city" />
          <Bg label="Naam directeur / CEO *" value={form.employer_ceo_name} onChange={(v) => update('employer_ceo_name', v)} testid="bg-ceo-name" />
          <Bg label="Functie directeur *" value={form.employer_ceo_function} onChange={(v) => update('employer_ceo_function', v)} testid="bg-ceo-function" />
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid="bg-save"
            className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Opslaan
          </button>
        </div>
      </div>

      <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
        ℹ️ Deze gegevens worden 1× ingevuld en automatisch toegevoegd aan elk gegenereerd contract.
        Wijzigingen werken meteen door bij nieuwe contracten.
      </div>
    </div>
  );
}

function Bg({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-amber focus:outline-none focus:ring-1 focus:ring-brand-amber/40"
      />
    </div>
  );
}

